// Editable label for the project banner (project name + role labels).
// Same interaction as EditableLabel: the text reads as a clickable
// field (hover = highlight only), clicking swaps in an <input>, and the
// edit surface appends a delete button when the caller wires one. No
// hover-revealed pencil/trash cluster.

import { useEffect, useRef, useState } from 'react';

import { HoverAction } from './HoverAction.tsx';

export function BannerLabel({
  text,
  onSave,
  onDelete,
}: {
  text: string;
  onSave: (next: string) => void;
  /** Optional — when provided, a delete button appears in the edit
   *  surface (e.g. role labels; the project name has no delete). */
  onDelete?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(text);
  }, [text]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    setEditing(false);
    if (trimmed && trimmed !== text) {
      onSave(trimmed);
    } else {
      setDraft(text);
    }
  };

  if (editing) {
    return (
      <span className='snap-banner-label snap-banner-label--editing'>
        <input
          ref={inputRef}
          className='snap-banner-label__input'
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
              setDraft(text);
            }
          }}
          autoFocus
        />
        {onDelete ? (
          <span className='snap-edit-actions'>
            <HoverAction
              kind='delete'
              label='Delete'
              onClick={() => {
                setEditing(false);
                onDelete();
              }}
            />
          </span>
        ) : null}
      </span>
    );
  }

  return (
    <span
      className='snap-banner-label snap-banner-label--clickable'
      role='button'
      tabIndex={0}
      onClick={() => setEditing(true)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setEditing(true);
        }
      }}
    >
      <span className='snap-banner-label__text'>{text}</span>
    </span>
  );
}
